import styled, { css } from 'styled-components';
import { fontSize, fontWeight } from 'modules/theme';
import { pxToRem } from 'modules/v2/foundation/spacing';
import { themeGet } from 'modules/v2/foundation/theme';
const getVerticalPadding = (size) => {
    switch (size) {
        case 'xs':
            return themeGet('spacing.xs');
        case 'sm':
            return pxToRem(6);
        default:
            return themeGet('spacing.md');
    }
};
const outlinePadding = css `
  padding: ${(props) => getVerticalPadding(props.$size)} ${(props) => (props.$size === 'xs' ? pxToRem(8) : pxToRem(12))};
  padding-right: ${(props) => `calc(${pxToRem(props.$iconSize)} + ${themeGet('spacing.2xl')})`};
`;
const linePadding = css `
  padding: ${(props) => getVerticalPadding(props.$size)} 0;
  padding-right: ${(props) => `calc(${pxToRem(props.$iconSize)} + ${props.$size === 'xs' ? pxToRem(8) : themeGet('spacing.lg')})`};
`;
const placeholderStyles = css `
  font-style: ${themeGet('fontStyle.italic')};
  color: ${themeGet('colors.disabled.300')};
`;
const getFontSize = (size) => {
    if (size === 'xs' || size === 'sm') {
        return themeGet('fontSize.small');
    }
    return themeGet('fontSize.medium');
};
export const StyledSelect = styled.select `
  appearance: none; /* hide the native chevron icon */
  border: none;
  position: relative; /* makes it possible to open the select also when clicking on the Chevron icon */
  background-color: transparent;
  flex: 1 1 100%;
  font-size: ${(props) => getFontSize(props.$size)};
  line-height: ${themeGet('lineHeight.default')};
  color: ${themeGet('colors.text.300')};
  width: 100%;
  text-overflow: ellipsis;
  ${(props) => (props.$variant === 'outline' ? outlinePadding : linePadding)};
  ${(props) => props.$showPlaceholderStyle && placeholderStyles};

  &:focus-visible {
    outline: none;
  }

  &:disabled {
    color: ${themeGet('colors.disabled.200')};
  }
`;
export const Container = styled.div `
  position: relative;
  width: ${(props) => props.$stretch && '100%;'};
`;
const descriptionInvalidStyles = css `
  color: ${themeGet('colors.text.danger')};
`;
export const Description = styled.div `
  ${fontSize('tiny')};
  ${fontWeight('normal')};
  color: ${themeGet('colors.text.300')};
  display: inline-block;
  margin-top: ${themeGet('spacing.sm')};
  line-height: ${themeGet('lineHeight.neutralized')};

  &:has(+ * select:disabled) {
    color: ${themeGet('colors.disabled.200')};
  }

  &:has(+ * select:focus-visible) {
    color: ${(props) => !props.$invalid && themeGet('colors.primary.1000')};
  }

  ${(props) => props.$invalid && descriptionInvalidStyles};
`;
const outlineInputDisabledStyles = css `
  border: ${pxToRem(1)} dashed ${themeGet('colors.disabled.200')};
  box-shadow: none;
  margin: ${pxToRem(1)}; /* prevents the input border from jumping */

  svg {
    color: ${themeGet('colors.disabled.200')};
  }
`;
const outlineInputDefaultStyles = css `
  background-color: ${themeGet('colors.background.50')};
  box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.border.100')}; /* firefox fallback until :has selector is supported */

  &:has(select:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.border.100')};
  }

  &:hover:not(:has(select:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.border.200')};
  }

  &:has(select:focus-visible:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.border.300')};

    svg {
      color: ${themeGet('colors.primary.1000')};
    }
  }

  &:has(select:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.border.300')};
  }

  &:has(select:disabled) {
    ${outlineInputDisabledStyles};
  }
`;
const outlineInputHighlightStyles = css `
  background-color: ${themeGet('colors.blue.50')};
  box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.blue.500')};

  &:hover:not(:has(select:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.blue.500')};
  }

  &:has(select:focus-visible:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.blue.500')};

    svg {
      color: ${themeGet('colors.blue.500')};
    }
  }

  &:has(select:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.blue.500')};
  }

  &:has(select:disabled) {
    ${outlineInputDisabledStyles};
  }
`;
const outlineInputInvalidStyles = css `
  background-color: ${themeGet('colors.background.50')};
  box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.text.danger')};

  &:hover:not(:has(select:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.text.danger')};
  }

  &:has(select:focus-visible:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.text.danger')};

    svg {
      color: ${themeGet('colors.text.danger')};
    }
  }

  &:has(select:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.text.danger')};
  }

  &:has(select:disabled) {
    ${outlineInputDisabledStyles};
  }
`;
const lineInputDisabledStyles = css `
  border-bottom: ${pxToRem(1)} dashed ${themeGet('colors.disabled.200')};
  box-shadow: none;
  margin: ${pxToRem(2)} ${pxToRem(2)} ${pxToRem(1)}; /* /* prevents the input border from jumping */

  svg {
    color: ${themeGet('colors.disabled.200')};
  }
`;
const lineInputDefaultStyles = css `
  background-color: transparent;
  box-shadow: 0 ${pxToRem(1)} 0 0 ${themeGet('colors.border.100')}; /* firefox fallback until :has selector is supported */

  &:has(select:not(:disabled)) {
    box-shadow: 0 ${pxToRem(1)} 0 0 ${themeGet('colors.border.200')};
  }

  &:hover:not(:has(select:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${themeGet('colors.border.300')};
  }

  &:has(select:focus-visible:not(:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${themeGet('colors.border.300')};

    svg {
      color: ${themeGet('colors.primary.1000')};
    }
  }

  &:has(select:active:not(:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${themeGet('colors.border.300')};
  }

  &:has(select:disabled) {
    ${lineInputDisabledStyles};
  }
`;
const lineInputHighlightStyles = css `
  box-shadow: 0 ${pxToRem(1)} 0 0 ${themeGet('colors.blue.500')};

  &:hover:not(:has(select:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${themeGet('colors.blue.500')};
  }

  &:has(select:focus-visible:not(:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${themeGet('colors.blue.500')};

    svg {
      color: ${themeGet('colors.blue.500')};
    }
  }

  &:has(select:active:not(:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${themeGet('colors.blue.500')};
  }

  &:has(select:disabled) {
    ${lineInputDisabledStyles};
  }
`;
const lineInputInvalidStyles = css `
  box-shadow: 0 ${pxToRem(1)} 0 0 ${themeGet('colors.text.danger')};

  &:hover:not(:has(select:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${themeGet('colors.text.danger')};
  }

  &:has(select:focus-visible:not(:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${themeGet('colors.text.danger')};

    svg {
      color: ${themeGet('colors.text.danger')};
    }
  }

  &:has(select:active:not(:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${themeGet('colors.text.danger')};
  }

  &:has(select:disabled) {
    ${lineInputDisabledStyles};
  }
`;
const outlineInputStyles = css `
  border-radius: ${themeGet('borderRadius.md')};
  ${(props) => props.$appearance === undefined && !props.$invalid && outlineInputDefaultStyles};
  ${(props) => props.$appearance === 'highlight' && outlineInputHighlightStyles};
  ${(props) => props.$invalid && outlineInputInvalidStyles};
`;
const lineInputStyles = css `
  ${(props) => props.$appearance === undefined && !props.$invalid && lineInputDefaultStyles};
  ${(props) => props.$appearance === 'highlight' && lineInputHighlightStyles};
  ${(props) => props.$invalid && lineInputInvalidStyles};
`;
export const SelectContainer = styled.div `
  ${(props) => props.$variant === 'outline' && outlineInputStyles};
  ${(props) => props.$variant === 'line' && lineInputStyles};
  display: flex;
  box-sizing: border-box;
  position: relative;
  margin: ${pxToRem(2)}; /* prevent box-shadow border from being hidden/covered in case of container not having any padding */
  transition: box-shadow 0.15s;
`;
export const IconContainer = styled.div `
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 50%;
  right: ${(props) => (props.$variant === 'outline' ? themeGet('spacing.lg') : 0)};
  transform: translateY(-50%);
`;
